import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { store } from 'store';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery, CircularProgress } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import { drawerWidth } from 'store/constant';
import { REMOVE_USER, SET_MENU, SET_USER, SET_NOTIFICATION } from 'store/actions';
import { validateToken } from '../../services/authService';
import { checkHealth } from '../../services/orgsService';
import Notification from 'ui-component/extended/Notification';

// assets
import { IconChevronRight } from '@tabler/icons';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  ...theme.typography.mainContent,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  transition: theme.transitions.create(
    'margin',
    open
      ? {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen
        }
      : {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen
        }
  ),
  [theme.breakpoints.up('md')]: {
    marginLeft: open ? 0 : -(drawerWidth - 20),
    width: `calc(100% - ${drawerWidth}px)`
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '20px',
    width: `calc(100% - ${drawerWidth}px)`,
    padding: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: '10px',
    width: `calc(100% - ${drawerWidth}px)`,
    padding: '16px',
    marginRight: '10px'
  }
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  
  // Handle left drawer
  const leftDrawerOpened = useSelector((state) => state.customization.opened);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  
  const handleLeftDrawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
  };
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const checkOrgHealth = async (orgId) => {
    try {
      const healthStatus = await checkHealth(orgId);
      if (!healthStatus.isHealthy) {
        dispatch({
          type: SET_NOTIFICATION,
          payload: {
            type: 'warning',
            message: healthStatus.message
          }
        });
      }
    } catch (error) {
      console.error('Failed to check org health:', error);
    }
  };

  const checkAuth = async () => {
    try {
      const response = await validateToken();
      const orgId = JSON.parse(localStorage.getItem('orgId'));
      dispatch({ 
        type: SET_USER, 
        payload: { 
          userId: response.userId, 
          name: response.firstName + ' ' + response.lastName,
          orgId: orgId,
          role: response.role
        } 
      });
      setIsAuthenticated(response);
      setIsLoading(false);
      
      // Check org health after successful authentication
      if (orgId) {
        await checkOrgHealth(orgId);
      }
    } catch (e) {
      dispatch({ type: REMOVE_USER });
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  };

  // Check auth on component mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Check health on every route change
  useEffect(() => {
    if (isAuthenticated && user.orgId) {
      checkOrgHealth(user.orgId);
    }
  }, [location.pathname, isAuthenticated, user.orgId]);

  return isLoading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  ) : isAuthenticated ? (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: theme.palette.background.default,
          transition: leftDrawerOpened ? theme.transitions.create('width') : 'none'
        }}
      >
        <Toolbar>
          <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
        </Toolbar>
      </AppBar>

      {/* drawer */}
      <Sidebar drawerOpen={!matchDownMd ? leftDrawerOpened : !leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />

      {/* main content */}
      <Main theme={theme} open={leftDrawerOpened}>
        {/* breadcrumb */}
        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
        <Outlet />
        {/* Notification */}
        <Notification />
      </Main>
    </Box>
  ) : (
    <Navigate to="/login" replace />
  );
};

export default MainLayout;
